import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getPostLoginPath } from "lib/utils/authNavigation";
import { useAppStore } from "../../lib/store/useAppStore";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff } from "lucide-react";
import LocaleSwitcher from "components/LocaleSwitcher";
import {
  checkSignupApproverEmail,
  checkSignupEmailAvailability,
  requestSignup,
} from "lib/api/requestSignup";

const SIGNUP_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SIGNUP_PASSWORD_PATTERN =
  /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
const SIGNUP_ENABLED = false;

const SIGNUP_COUNTRY_OPTIONS = [
  { value: "KR", label: "대한민국 (KR)" },
  { value: "US", label: "United States (US)" },
  { value: "VN", label: "Vietnam (VN)" },
  { value: "JP", label: "Japan (JP)" },
  { value: "CN", label: "China (CN)" },
];

const INITIAL_SIGNUP_FORM = {
  email: "",
  password: "",
  passwordConfirm: "",
  approverEmail: "",
  companyName: "",
  country: "",
  userName: "",
};

const INITIAL_SIGNUP_EMAIL_CHECK = {
  email: "",
  status: "idle",
};

// _________________login Form_________________
export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [sessionConflict, setSessionConflict] = useState(null);
  const [forceLoginLoading, setForceLoginLoading] = useState(false);
  const [isSignupOpen, setIsSignupOpen] = useState(false);
  const [signupForm, setSignupForm] = useState(INITIAL_SIGNUP_FORM);
  const [signupLoading, setSignupLoading] = useState(false);
  const [isSignupPasswordVisible, setIsSignupPasswordVisible] =
    useState(false);
  const [isSignupPasswordConfirmVisible, setIsSignupPasswordConfirmVisible] =
    useState(false);
  const [signupEmailCheck, setSignupEmailCheck] = useState(
    INITIAL_SIGNUP_EMAIL_CHECK,
  );
  const [signupEmailCheckLoading, setSignupEmailCheckLoading] = useState(false);
  const [signupApproverCheckLoading, setSignupApproverCheckLoading] =
    useState(false);
  const { t } = useTranslation();
  const login = useAppStore((s) => s.login);
  const authLoading = useAppStore((s) => s.authLoading);
  const showAlert = useAppStore((s) => s.showAlert);

  const navigate = useNavigate();
  const { state: navigationState } = useLocation();

  const handleLoginResult = (result) => {
    if (!result?.success) {
      if (
        result?.code === "CONCURRENT_SESSION_EXISTS" ||
        result?.data?.requiresSessionTermination
      ) {
        setSessionConflict(result);
        return false;
      }

      showAlert({
        text: result?.message,
        messageKey: result?.messageKey,
        messageParams: result?.messageParams,
        success: false,
        autoClose: true,
      });
      return false;
    }

    navigate(
      getPostLoginPath(navigationState?.from, result.last_selected_project),
      { replace: true },
    );
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const result = await login(email, password);
    handleLoginResult(result);
  };

  const handleForceLogin = async () => {
    if (forceLoginLoading) return;

    setForceLoginLoading(true);
    const result = await login(email, password, { force: true });
    setForceLoginLoading(false);

    if (result?.success) {
      setSessionConflict(null);
    }

    handleLoginResult(result);
  };

  const showSignupAlert = (text, success = false) => {
    showAlert({
      text,
      success,
      autoClose: true,
    });
  };

  const getNormalizedSignupEmail = () =>
    signupForm.email.trim().toLowerCase();

  const getSignupEmailCheckStatus = () => {
    const email = getNormalizedSignupEmail();
    if (!email || signupEmailCheck.email !== email) {
      return "idle";
    }

    return signupEmailCheck.status;
  };

  const handleSignupFormChange = (field, value) => {
    setSignupForm((current) => ({
      ...current,
      [field]: value,
    }));

    if (field === "email") {
      setSignupEmailCheck(INITIAL_SIGNUP_EMAIL_CHECK);
    }
  };

  const handleCloseSignup = () => {
    if (signupLoading || signupEmailCheckLoading || signupApproverCheckLoading) {
      return;
    }
    setIsSignupOpen(false);
    setIsSignupPasswordVisible(false);
    setIsSignupPasswordConfirmVisible(false);
  };

  const handleSignupEmailCheck = async () => {
    if (signupEmailCheckLoading || signupLoading) return;

    const email = getNormalizedSignupEmail();
    if (!email || !SIGNUP_EMAIL_PATTERN.test(email)) {
      setSignupEmailCheck({
        email,
        status: "invalid",
      });
      showSignupAlert("올바른 이메일 주소를 입력해 주세요.");
      return;
    }

    setSignupEmailCheckLoading(true);
    const result = await checkSignupEmailAvailability(email);
    setSignupEmailCheckLoading(false);

    if (!result.success) {
      setSignupEmailCheck({
        email,
        status: "error",
      });
      showSignupAlert(result.message || "이메일 중복 확인에 실패했습니다.");
      return;
    }

    if (result.data?.available) {
      setSignupEmailCheck({
        email,
        status: "available",
      });
      showSignupAlert("사용 가능한 이메일입니다.", true);
      return;
    }

    const status = result.data?.hasPendingRequest ? "pending" : "duplicate";
    setSignupEmailCheck({
      email,
      status,
    });
    showSignupAlert(
      status === "pending"
        ? "가입 승인 대기 중인 이메일입니다."
        : "이미 사용 중인 이메일입니다.",
    );
  };

  const handleSignupSubmit = async (event) => {
    event.preventDefault();
    if (signupLoading || signupEmailCheckLoading || signupApproverCheckLoading) {
      return;
    }

    const payload = {
      email: getNormalizedSignupEmail(),
      password: signupForm.password,
      approverEmail: signupForm.approverEmail.trim(),
      companyName: signupForm.companyName.trim(),
      country: signupForm.country.trim(),
      userName: signupForm.userName.trim(),
    };

    if (
      !payload.email ||
      !payload.password ||
      !payload.approverEmail ||
      !payload.companyName ||
      !payload.country ||
      !payload.userName ||
      !signupForm.passwordConfirm
    ) {
      showSignupAlert(t("loginForm.signupRequiredMessage"));
      return;
    }

    if (!SIGNUP_EMAIL_PATTERN.test(payload.email)) {
      showSignupAlert("올바른 이메일 주소를 입력해 주세요.");
      return;
    }

    if (getSignupEmailCheckStatus() !== "available") {
      showSignupAlert("이메일 중복 확인을 먼저 진행해 주세요.");
      return;
    }

    if (!SIGNUP_PASSWORD_PATTERN.test(payload.password)) {
      showSignupAlert(
        "비밀번호는 8자 이상 영문, 숫자, 특수문자 조합으로 입력해 주세요.",
      );
      return;
    }

    if (payload.password !== signupForm.passwordConfirm) {
      showSignupAlert("비밀번호와 비밀번호 확인이 일치하지 않습니다.");
      return;
    }

    if (!SIGNUP_EMAIL_PATTERN.test(payload.approverEmail)) {
      showSignupAlert("가입 승인자의 이메일 주소를 확인해 주세요.");
      return;
    }

    setSignupApproverCheckLoading(true);
    const approverCheckResult = await checkSignupApproverEmail(
      payload.approverEmail.trim().toLowerCase(),
    );
    setSignupApproverCheckLoading(false);

    if (
      !approverCheckResult.success ||
      approverCheckResult.data?.eligible !== true
    ) {
      showSignupAlert("가입 승인자를 찾을 수 없습니다. 이메일 주소를 확인해 주세요.");
      return;
    }

    setSignupLoading(true);
    const result = await requestSignup(payload);
    setSignupLoading(false);

    showAlert({
      text:
        result.success
          ? "가입 요청이 접수되었습니다.\n승인자의 승인 후 계정 사용이 가능합니다."
          : result.message || t("loginForm.signupFailedMessage"),
      success: result.success,
      autoClose: true,
    });

    if (result.success) {
      setSignupForm(INITIAL_SIGNUP_FORM);
      setSignupEmailCheck(INITIAL_SIGNUP_EMAIL_CHECK);
      setIsSignupPasswordVisible(false);
      setIsSignupPasswordConfirmVisible(false);
      setIsSignupOpen(false);
    }
  };

  const signupEmailStatus = getSignupEmailCheckStatus();
  const signupEmailStatusMessage =
    signupEmailStatus === "available"
      ? "사용 가능한 이메일입니다."
      : signupEmailStatus === "duplicate"
        ? "이미 사용 중인 이메일입니다."
        : signupEmailStatus === "pending"
          ? "이미 승인 대기 중인 요청이 있습니다."
          : signupEmailStatus === "invalid"
            ? "올바른 이메일 주소를 입력해 주세요."
            : "";
  const signupEmailStatusTone =
    signupEmailStatus === "available" ? "success" : "error";
  const signupBusy =
    signupLoading || signupEmailCheckLoading || signupApproverCheckLoading;

  return (
    <div className="loginForm">
      <img src="/images/mainLogo.png" alt="" />
      <form onSubmit={(e) => handleSubmit(e)}>
        <label htmlFor="inputLoginId">{t("loginForm.email")}</label>
        <input
          id="inputLoginId"
          onChange={(e) => setEmail(e.target.value)}
          value={email}
          placeholder={t("loginForm.emailPlaceholder")}
          type="text"
        />
        <label htmlFor="inputLoginPw">{t("loginForm.password")}</label>
        <input
          id="inputLoginPw"
          onChange={(e) => setPassword(e.target.value)}
          value={password}
          placeholder={t("loginForm.passwordPlaceholder")}
          type="password"
        />
        <div className="loginLocaleRow">
          <LocaleSwitcher />
        </div>

        <button type="submit" disabled={authLoading || forceLoginLoading}>
          {t("loginForm.submitButton")}
        </button>
        {SIGNUP_ENABLED ? (
          <button
            className="loginSignupButton"
            disabled={authLoading || forceLoginLoading}
            onClick={() => setIsSignupOpen(true)}
            type="button"
          >
            {t("loginForm.signupButton")}
          </button>
        ) : null}
        <p>Contact: info@checkguard.co.kr</p>
      </form>
      {SIGNUP_ENABLED && isSignupOpen ? (
        <div
          className="loginSignupDialog"
          role="presentation"
          onMouseDown={handleCloseSignup}
        >
          <form
            className="loginSignupDialog__card"
            onMouseDown={(event) => event.stopPropagation()}
            onSubmit={handleSignupSubmit}
            role="dialog"
            aria-modal="true"
            aria-labelledby="login-signup-title"
          >
            <div className="loginSignupDialog__header">
              <div>
                <h2 id="login-signup-title">
                  {t("loginForm.signupTitle")}
                </h2>
              </div>
              <button
                aria-label={t("loginForm.signupClose")}
                className="loginSignupDialog__close"
                disabled={signupBusy}
                onClick={handleCloseSignup}
                type="button"
              >
                ×
              </button>
            </div>

            <div className="loginSignupDialog__fields">
              <label className="loginSignupDialog__field">
                <span>{t("loginForm.signupEmail")}</span>
                <div className="loginSignupDialog__inputRow">
                  <input
                    autoComplete="email"
                    autoFocus
                    onChange={(event) =>
                      handleSignupFormChange("email", event.target.value)
                    }
                    placeholder={t("loginForm.signupEmailPlaceholder")}
                    type="email"
                    value={signupForm.email}
                  />
                  <button
                    className="loginSignupDialog__checkButton"
                    disabled={signupBusy}
                    onClick={handleSignupEmailCheck}
                    type="button"
                  >
                    {signupEmailCheckLoading ? "확인 중" : "중복 확인"}
                  </button>
                </div>
                {signupEmailStatusMessage ? (
                  <small
                    className={`loginSignupDialog__status loginSignupDialog__status--${signupEmailStatusTone}`}
                  >
                    {signupEmailStatusMessage}
                  </small>
                ) : null}
              </label>

              <label className="loginSignupDialog__field">
                <span>{t("loginForm.signupPassword")}</span>
                <div className="loginSignupDialog__passwordField">
                  <input
                    autoComplete="new-password"
                    onChange={(event) =>
                      handleSignupFormChange("password", event.target.value)
                    }
                    placeholder="8자 이상 영문, 숫자, 특수문자 조합"
                    type={isSignupPasswordVisible ? "text" : "password"}
                    value={signupForm.password}
                  />
                  <button
                    aria-label={
                      isSignupPasswordVisible
                        ? "비밀번호 숨기기"
                        : "비밀번호 표시"
                    }
                    className="loginSignupDialog__passwordToggle"
                    onClick={() =>
                      setIsSignupPasswordVisible((current) => !current)
                    }
                    title={
                      isSignupPasswordVisible
                        ? "비밀번호 숨기기"
                        : "비밀번호 표시"
                    }
                    type="button"
                  >
                    {isSignupPasswordVisible ? (
                      <Eye size={17} aria-hidden="true" />
                    ) : (
                      <EyeOff size={17} aria-hidden="true" />
                    )}
                  </button>
                </div>
              </label>

              <label className="loginSignupDialog__field">
                <span>비밀번호 확인</span>
                <div className="loginSignupDialog__passwordField">
                  <input
                    autoComplete="new-password"
                    onChange={(event) =>
                      handleSignupFormChange(
                        "passwordConfirm",
                        event.target.value,
                      )
                    }
                    placeholder="비밀번호를 한 번 더 입력"
                    type={
                      isSignupPasswordConfirmVisible ? "text" : "password"
                    }
                    value={signupForm.passwordConfirm}
                  />
                  <button
                    aria-label={
                      isSignupPasswordConfirmVisible
                        ? "비밀번호 확인 숨기기"
                        : "비밀번호 확인 표시"
                    }
                    className="loginSignupDialog__passwordToggle"
                    onClick={() =>
                      setIsSignupPasswordConfirmVisible((current) => !current)
                    }
                    title={
                      isSignupPasswordConfirmVisible
                        ? "비밀번호 확인 숨기기"
                        : "비밀번호 확인 표시"
                    }
                    type="button"
                  >
                    {isSignupPasswordConfirmVisible ? (
                      <Eye size={17} aria-hidden="true" />
                    ) : (
                      <EyeOff size={17} aria-hidden="true" />
                    )}
                  </button>
                </div>
              </label>

              <label className="loginSignupDialog__field">
                <span>{t("loginForm.signupUserName")}</span>
                <input
                  autoComplete="name"
                  onChange={(event) =>
                    handleSignupFormChange("userName", event.target.value)
                  }
                  placeholder={t("loginForm.signupUserNamePlaceholder")}
                  type="text"
                  value={signupForm.userName}
                />
              </label>

              <label className="loginSignupDialog__field">
                <span>{t("loginForm.signupCompanyName")}</span>
                <input
                  onChange={(event) =>
                    handleSignupFormChange("companyName", event.target.value)
                  }
                  placeholder={t("loginForm.signupCompanyNamePlaceholder")}
                  type="text"
                  value={signupForm.companyName}
                />
              </label>

              <label className="loginSignupDialog__field">
                <span>{t("loginForm.signupCountry")}</span>
                <select
                  onChange={(event) =>
                    handleSignupFormChange("country", event.target.value)
                  }
                  value={signupForm.country}
                >
                  <option value="">국가 선택</option>
                  {SIGNUP_COUNTRY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="loginSignupDialog__field">
                <span>{t("loginForm.signupApproverEmail")}</span>
                <input
                  autoComplete="email"
                  onChange={(event) =>
                    handleSignupFormChange(
                      "approverEmail",
                      event.target.value,
                    )
                  }
                  placeholder={t("loginForm.signupApproverEmailPlaceholder")}
                  type="email"
                  value={signupForm.approverEmail}
                />
              </label>
            </div>

            <div className="loginSignupDialog__actions">
              <button
                className="loginSignupDialog__secondary"
                disabled={signupBusy}
                onClick={handleCloseSignup}
                type="button"
              >
                {t("loginForm.signupCancel")}
              </button>
              <button
                className="loginSignupDialog__primary"
                disabled={signupBusy}
                type="submit"
              >
                {signupBusy
                  ? t("loginForm.signupSubmitting")
                  : t("loginForm.signupSubmit")}
              </button>
            </div>
          </form>
        </div>
      ) : null}
      {sessionConflict ? (
        <div
          className="loginSessionDialog"
          role="presentation"
          onMouseDown={() => setSessionConflict(null)}
        >
          <div
            className="loginSessionDialog__card"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="login-session-title"
            aria-describedby="login-session-description"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="loginSessionDialog__title" id="login-session-title">
              {t("loginForm.concurrentSessionTitle")}
            </div>
            <div
              className="loginSessionDialog__description"
              id="login-session-description"
            >
              {t("loginForm.concurrentSessionMessage")}
            </div>
            <div className="loginSessionDialog__actions">
              <button
                type="button"
                className="loginSessionDialog__secondary"
                onClick={() => setSessionConflict(null)}
              >
                {t("loginForm.concurrentSessionCancel")}
              </button>
              <button
                type="button"
                className="loginSessionDialog__primary"
                disabled={forceLoginLoading}
                onClick={handleForceLogin}
              >
                {forceLoginLoading
                  ? t("loginForm.concurrentSessionProcessing")
                  : t("loginForm.concurrentSessionConfirm")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
